/**
 * POM-6536 — every lot weighed against the whole review batch, the way the commit weighs it.
 *
 * Mirrors core's checkBatchLotCapacity (POM-6535) so the grid and the picker say before the commit
 * what the commit would answer: rows that keep their lot count first, then the staged changes in
 * batch order. A change the lot cannot cover takes nothing unless its lot is claimed, because core
 * refuses it. Dispensed rows are skipped: their allocation already took the stock out of
 * remainingQty, and core skips them for the same reason.
 *
 * Pure: no React, no network.
 */

// numeric(12,4) quantities, as core compares them.
const QTY_EPSILON = 1e-6;

const isWeighed = (item) => !item.isOtc && !!item.productId && !item.isDispensed;

/** Lot id -> what the review snapshot knows of it: its balance and its number. */
const lotFactsById = ({ items, availability }) => {
  const facts = new Map();
  (items ?? []).forEach((item) =>
    (item.recordedLots ?? []).forEach((lot) => {
      if (lot.inventoryLotId) facts.set(lot.inventoryLotId, lot);
    })
  );
  (availability ?? []).forEach((entry) =>
    (entry.groups ?? []).forEach((group) =>
      (group.lots ?? []).forEach((lot) => facts.set(lot.inventoryLotId, lot))
    )
  );

  return facts;
};

/**
 * The lots one row asks of. Core skips hand-recorded lots on a change, which draw on no balance,
 * but counts every recorded entry that names a lot; this does the same so the two agree. Only a
 * single-lot, stock-picked row can carry the shortfall permission.
 */
const demandsOf = ({ item, change }) => {
  if (change) {
    const allocations = change.lotAllocations ?? [];

    return {
      keepsLot: false,
      canCarryClaim: allocations.length === 1 && allocations[0].isManualEntry !== true,
      lots: allocations
        .filter((lot) => lot.isManualEntry !== true && !!lot.inventoryLotId)
        .map((lot) => ({
          inventoryLotId: lot.inventoryLotId,
          quantity: Number(lot.quantity || 0),
        })),
    };
  }

  const recorded = item.recordedLots ?? [];
  const canCarryClaim = recorded.length === 1 && recorded[0].isManualEntry !== true;

  return {
    keepsLot: true,
    canCarryClaim,
    holdsPermission: canCarryClaim && recorded[0].allowShortfallAsUnverified === true,
    lots: recorded
      .filter((lot) => !!lot.inventoryLotId)
      .map((lot) => ({ inventoryLotId: lot.inventoryLotId, quantity: Number(lot.quantity || 0) })),
  };
};

const weighedRows = ({ items, changes }) =>
  (items ?? [])
    .filter(isWeighed)
    .map((item) => ({ item, ...demandsOf({ item, change: changes?.[item.requestLineItemId] }) }));

/**
 * Kept rows that already hold the permission go first. At fill any row may take the stock first,
 * and one holding the permission is covered whichever it is, so the rows past the stock must be
 * the ones that are not: those are the rows that would fill with no stock and no claim. Then the
 * other kept rows, then the changes, each in batch order.
 */
const inWeighingOrder = (rows) => [
  ...rows.filter((row) => row.keepsLot && row.holdsPermission),
  ...rows.filter((row) => row.keepsLot && !row.holdsPermission),
  ...rows.filter((row) => !row.keepsLot),
];

/** Lot id -> may it be claimed: core refuses the whole lot's claim when one row cannot carry it. */
const claimableByLotId = (rows) => {
  const claimable = new Map();
  rows.forEach(({ canCarryClaim, lots }) =>
    lots.forEach(({ inventoryLotId }) =>
      claimable.set(inventoryLotId, (claimable.get(inventoryLotId) ?? true) && canCarryClaim)
    )
  );

  return claimable;
};

/**
 * The lots this batch claims: a staged change the operator confirmed short, or a row that keeps
 * its own lot and was confirmed as a claim. Sent to core as claimShortfallLotIds.
 */
export const stagedClaimLotIds = ({ changes, claims }) =>
  new Set([
    ...Object.values(claims ?? {}),
    ...Object.values(changes ?? {}).flatMap((change) =>
      (change.lotAllocations ?? [])
        .filter((lot) => lot.allowShortfallAsUnverified === true && !!lot.inventoryLotId)
        .map((lot) => lot.inventoryLotId)
    ),
  ]);

/**
 * Per lot: its balance, what the batch takes of it, how far short it falls, whether every row on
 * it can carry the permission, and whether it is claimed (a claim is staged AND it can be). Per
 * row: the first lot it cannot be covered on, by how much, and what the row asks of that lot —
 * only part of its fill when it holds two lots.
 */
export const weighBatchLots = ({ items, availability, changes, claims }) => {
  const facts = lotFactsById({ items, availability });
  const claimedLotIds = stagedClaimLotIds({ changes, claims });
  const rows = weighedRows({ items, changes });
  const claimable = claimableByLotId(rows);
  const byLot = new Map();
  const shortByLineItem = new Map();

  inWeighingOrder(rows).forEach(({ item, keepsLot, holdsPermission, lots }) => {
    lots.forEach(({ inventoryLotId, quantity }) => {
      const canClaim = claimable.get(inventoryLotId);
      const entry = byLot.get(inventoryLotId) ?? {
        inventoryLotId,
        lotNumber: facts.get(inventoryLotId)?.lotNumber ?? '',
        remainingQty: Number(facts.get(inventoryLotId)?.remainingQty ?? 0),
        usedQty: 0,
        rowCount: 0,
        canClaim,
        isClaimed: canClaim && claimedLotIds.has(inventoryLotId),
        isEveryRowPermitted: true,
      };
      const freeQty = Math.max(0, entry.remainingQty - entry.usedQty);
      const shortQty = quantity - freeQty > QTY_EPSILON ? quantity - freeQty : 0;

      if (shortQty > 0 && !shortByLineItem.has(item.requestLineItemId)) {
        shortByLineItem.set(item.requestLineItemId, {
          inventoryLotId,
          shortQty,
          quantityOnLot: quantity,
        });
      }
      if (!shortQty || keepsLot || entry.isClaimed) entry.usedQty += quantity;
      entry.rowCount += 1;
      entry.isEveryRowPermitted = entry.isEveryRowPermitted && keepsLot && !!holdsPermission;
      byLot.set(inventoryLotId, entry);
    });
  });

  const lots = new Map(
    [...byLot].map(([lotId, entry]) => [
      lotId,
      { ...entry, shortQty: Math.max(0, entry.usedQty - entry.remainingQty) },
    ])
  );

  return { lots, shortByLineItem };
};

/**
 * Claims whose row the batch no longer leaves short, or whose lot can no longer be claimed (a row
 * holding two lots was put back on it), drop out, so a claim cannot outlive its reason. Dropping
 * one frees nothing a kept row counts on, so one re-weigh settles it.
 */
export const settleBatchClaims = ({ items, availability, changes, claims }) => {
  const first = weighBatchLots({ items, availability, changes, claims });
  const activeClaims = Object.fromEntries(
    Object.entries(claims ?? {}).filter(
      ([requestLineItemId, inventoryLotId]) =>
        first.shortByLineItem.has(requestLineItemId) && !!first.lots.get(inventoryLotId)?.canClaim
    )
  );

  if (Object.keys(activeClaims).length === Object.keys(claims ?? {}).length) {
    return { usage: first, activeClaims };
  }

  return {
    usage: weighBatchLots({ items, availability, changes, claims: activeClaims }),
    activeClaims,
  };
};

/**
 * What the batch leaves on each lot for one row, as Change NDC & lot shows it.
 *
 * Picking the one stock lot the row records keeps it, so the row waits only for the rows ahead of
 * it in the weighing order. Any other pick, including one of a multi-lot row's own lots, is a
 * change, and a change comes after every row already on the lot. `canClaim` is about the other
 * rows: the pick itself is one stock lot, so it can always carry the permission.
 */
export const batchShareForRow = ({ items, availability, changes, item }) => {
  const facts = lotFactsById({ items, availability });
  const rows = weighedRows({ items, changes });
  const edited = { item, ...demandsOf({ item, change: null }) };
  // The edited row is weighed as if it kept its recorded lot, at its own place in the batch.
  const isEdited = (row) => row.item.requestLineItemId === item.requestLineItemId;
  const weighingOrder = inWeighingOrder(
    rows.some(isEdited) ? rows.map((row) => (isEdited(row) ? edited : row)) : [...rows, edited]
  );
  const editedPosition = weighingOrder.indexOf(edited);
  const keptLotId = edited.canCarryClaim ? item.recordedLots[0].inventoryLotId : null;
  const others = weighingOrder.filter((row) => row !== edited);
  const lotIds = (availability ?? [])
    .filter(
      (entry) =>
        entry.productId === item.productId &&
        entry.fulfillingPharmacyId === item.fulfillingPharmacyId
    )
    .flatMap((entry) => (entry.groups ?? []).flatMap((group) => group.lots ?? []))
    .map((lot) => lot.inventoryLotId);

  return new Map(
    lotIds.map((lotId) => {
      const onLot = others.filter((row) => row.lots.some((lot) => lot.inventoryLotId === lotId));
      const ahead =
        lotId === keptLotId
          ? onLot.filter((row) => row.keepsLot && weighingOrder.indexOf(row) < editedPosition)
          : onLot;
      const usedQty = ahead.reduce(
        (sum, row) =>
          sum +
          row.lots
            .filter((lot) => lot.inventoryLotId === lotId)
            .reduce((lotSum, lot) => lotSum + lot.quantity, 0),
        0
      );
      const stockQty = Number(facts.get(lotId)?.remainingQty ?? 0);

      return [
        lotId,
        {
          stockQty,
          usedQty,
          usedRowCount: ahead.length,
          freeQty: Math.max(0, stockQty - usedQty),
          canClaim: onLot.every((row) => row.canCarryClaim),
        },
      ];
    })
  );
};

/**
 * POM-6537 — what each lot has left once every row the batch puts on it is counted, and whether it
 * may be claimed. Every demand counts, short or not, so `freeQty` goes below zero when the batch
 * already asks more of the lot than it holds.
 *
 * Returns a lookup, because a lot no row is on yet still has its shelf balance to offer. A row about
 * to move onto the lot can be excluded: its own share comes back, and a second lot it holds no
 * longer stops the claim.
 */
export const lotBalancesInBatch = ({ items, availability, changes }) => {
  const facts = lotFactsById({ items, availability });
  // Lot id -> requestLineItemId -> { quantity, canCarryClaim }.
  const demandsByLot = new Map();
  weighedRows({ items, changes }).forEach(({ item, canCarryClaim, lots }) =>
    lots.forEach(({ inventoryLotId, quantity }) => {
      const demands = demandsByLot.get(inventoryLotId) ?? new Map();
      const earlier = demands.get(item.requestLineItemId)?.quantity ?? 0;
      demands.set(item.requestLineItemId, { quantity: earlier + quantity, canCarryClaim });
      demandsByLot.set(inventoryLotId, demands);
    })
  );

  return (inventoryLotId, excludedLineItemId = null) => {
    const others = [...(demandsByLot.get(inventoryLotId) ?? new Map())]
      .filter(([requestLineItemId]) => requestLineItemId !== excludedLineItemId)
      .map(([, demand]) => demand);

    return {
      freeQty:
        Number(facts.get(inventoryLotId)?.remainingQty ?? 0) -
        others.reduce((sum, demand) => sum + demand.quantity, 0),
      canClaim: others.every((demand) => demand.canCarryClaim),
    };
  };
};

/**
 * The picker's lots with the batch taken off. `remainingQty` becomes what this row may take, so
 * the shortfall maths downstream (the staged permission, the notice, the label preview) counts the
 * other rows without knowing about them; `stockQty` keeps the shelf figure for display.
 *
 * Applied AFTER the picker's own selectability rule, which judges the shelf: a lot the batch has
 * used up is still offered when a claim is possible. Without one, or when another row on the lot
 * cannot carry the permission, a lot the batch leaves too little of is not offered at all.
 */
export const withBatchShare = ({ lotsByNdc, shareByLotId, fillQuantity, shortfallAllowed }) => {
  if (!shareByLotId) return lotsByNdc;

  return new Map(
    [...lotsByNdc].map(([ndc, lots]) => [
      ndc,
      lots
        .map((lot) => {
          const share = shareByLotId.get(lot.inventoryLotId);
          if (!share) return { lot, canClaim: true };

          return {
            lot: {
              ...lot,
              remainingQty: share.freeQty,
              stockQty: lot.remainingQty,
              batchUsedQty: share.usedQty,
              batchUsedRowCount: share.usedRowCount,
            },
            canClaim: share.canClaim,
          };
        })
        .filter(
          ({ lot, canClaim }) =>
            (shortfallAllowed && canClaim) || Number(lot.remainingQty) >= Number(fillQuantity || 0)
        )
        .map(({ lot }) => lot),
    ])
  );
};
